import React from "react";
import {
  Column,
  Row,
  Spinner,
  Text,
  ExpandIcon,
  IconButton,
  TextWithTooltip,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { useElapsedTime } from "src/hooks/use-elapsed-time";
import * as time from "src/utils/time";

const THINKING_STEP_DESCRIPTION = "Thinking…";

export const Thinking = () => {
  return <ExpandableProgressSummary progressSteps={[]} />;
};

export const textShimmer = {
  shimmer: true,
  color: "text.tertiary",
  shimmerColor: "gray.400",
} as const;

export const ExpandableProgressSummary = ({
  progressSteps,
  onExpand,
  initialVariant = "default",
  startTime,
  description,
  rightActions,
  p = 0,
  mb = 0,
}: {
  progressSteps: { description: string; completed: boolean }[];
  onExpand?: () => void;
  initialVariant?: "default" | "full";
  startTime?: string;
  description?: string;
  rightActions?: React.ReactNode;
  p?: number;
  mb?: number;
}) => {
  // Always use the description of the last step, we don't really care if it's completed or not,
  // eg. reasoning steps will never be "in progress".
  const currentStepDescription =
    progressSteps[progressSteps.length - 1]?.description ??
    THINKING_STEP_DESCRIPTION;

  const completedCount = progressSteps.filter((step) => step.completed).length;

  const { millis: elapsedTimeMillis } = useElapsedTime(
    startTime ? new Date(startTime).getTime() : Date.now(),
  );

  const elapsedTime = time.formatDuration(
    {
      start: 0,
      end: elapsedTimeMillis,
    },
    {
      units: "short",
      granularity: "second",
    },
  );

  // If we've been working for more than a minute, swap to the full variant
  // This helps in cases where the original effort classification was wrong.
  const variant = elapsedTimeMillis > 60 * 1000 ? "full" : initialVariant;

  return (
    <Column gap={2} gridColumn="1/-1" p={p} mb={mb}>
      <Column gap={1}>
        <Row alignItems="space-between" gap={2} w="100%">
          <Row
            alignItems="center"
            gap={2}
            flex={1}
            overflow="hidden"
            color="text.tertiary"
            onClick={onExpand}
            cursor="pointer"
          >
            <Spinner size="sm" color="currentColor" />
            <TextWithTooltip fontWeight="medium" {...textShimmer}>
              {currentStepDescription}
            </TextWithTooltip>
          </Row>
          <Row gap={2}>
            {rightActions}
            {onExpand && (
              <IconButton
                aria-label="View details"
                icon={ExpandIcon}
                onClick={onExpand}
              />
            )}
          </Row>
        </Row>
        {variant === "full" && (
          <Row justify="space-between">
            {completedCount > 0 ? (
              <Text size="sm" color="text.secondary">
                {completedCount} {pluralize("action", completedCount)}{" "}
                completed…
              </Text>
            ) : (
              <Text size="sm" color="text.secondary">
                Just getting started…
              </Text>
            )}
            {startTime && elapsedTime && (
              <Text size="sm" color="text.secondary">
                {elapsedTime}
              </Text>
            )}
          </Row>
        )}
      </Column>
      {description && (
        <Text size="sm" color="text.secondary">
          {description}
        </Text>
      )}
    </Column>
  );
};
